import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TestScenario from './TestScenario';
import { selectCustomScenarios } from './scenarioMatcher';
import { saveCustomTestResult } from '../../utils/supabase';
import { useAuth } from '../../components/Auth/AuthContext';
import './styles/customTest.css';

/**
 * 自定义测试组件
 * 根据用户问卷结果选择合适的场景进行测试
 */
function CustomTest() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [scenarios, setScenarios] = useState([]);
  const [currentScenarioIndex, setCurrentScenarioIndex] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // 加载用户问卷数据并选择匹配的场景
  useEffect(() => {
    const loadScenariosForUser = () => {
      try {
        setIsLoading(true);
        // 从localStorage获取用户问卷数据
        const savedData = localStorage.getItem('personalQuestionnaireData');
        
        if (!savedData) {
          setError('No questionnaire data found. Please complete the questionnaire first.');
          setIsLoading(false);
          return;
        }
        
        const userData = JSON.parse(savedData);
        
        // 将问卷数据转换为场景匹配算法需要的格式
        const surveyData = {
          ageGroup: userData.ageRange,
          occupation: userData.occupation,
          improvementAreas: userData.skillsToImprove,
          preferredScenarios: userData.preferredScenarios,
          communicationStyle: userData.communicationStyle,
          personalityTraits: mapCommunicationStyleToPersonality(userData.communicationStyle),
          socialChallenges: mapSkillsToSocialChallenges(userData.skillsToImprove),
          learningGoals: mapSkillsToLearningGoals(userData.skillsToImprove)
        };
        
        // 使用匹配算法选择最适合的6个场景
        const matchedScenarios = selectCustomScenarios(surveyData);
        setScenarios(matchedScenarios);
        
        // 加载之前保存的回答（如果有）
        const savedResponses = {};
        matchedScenarios.forEach(scenario => {
          const savedResponse = localStorage.getItem(`scenario_response_${scenario.id}`);
          if (savedResponse) {
            savedResponses[scenario.id] = savedResponse;
          }
        });
        
        setUserResponses(savedResponses);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading scenarios:', error);
        setError('An error occurred while preparing your test. Please try again.');
        setIsLoading(false);
      }
    };
    
    loadScenariosForUser();
  }, []);

  // 将沟通风格映射到个性特点
  const mapCommunicationStyleToPersonality = (style) => {
    const styleMap = {
      'direct': ['direct', 'assertive'],
      'supportive': ['empathetic', 'supportive'],
      'analytical': ['analytical', 'reserved']
    };
    
    return styleMap[style] || [];
  };
  
  // 将技能映射到社交挑战
  const mapSkillsToSocialChallenges = (skills) => {
    const challengeMap = {
      'active-listening': ['communication'],
      'conflict-resolution': ['conflictResolution'],
      'public-speaking': ['publicSpeaking', 'socialAnxiety'],
      'negotiation': ['negotiations'],
      'empathy': ['relationships'],
      'leadership': ['leadership'],
      'networking': ['networking', 'smallTalk'],
      'assertiveness': ['assertiveness', 'boundaries'],
      'nonverbal': ['nonverbalCommunication']
    };
    
    return skills?.flatMap(skill => challengeMap[skill] || []) || [];
  };
  
  // 将技能映射到学习目标
  const mapSkillsToLearningGoals = (skills) => {
    const goalMap = {
      'active-listening': ['betterRelationships', 'empathy'],
      'conflict-resolution': ['conflictResolution', 'leadership'],
      'public-speaking': ['publicSpeaking', 'confidence'],
      'negotiation': ['negotiation', 'careerAdvancement'],
      'empathy': ['empathy', 'betterRelationships'],
      'leadership': ['leadership', 'influence'],
      'networking': ['networking', 'careerAdvancement'],
      'assertiveness': ['assertiveness', 'confidence'],
      'nonverbal': ['awareness', 'betterRelationships']
    };
    
    return skills?.flatMap(skill => goalMap[skill] || []) || [];
  };

  // 保存用户回答
  const handleSaveResponse = (scenarioId, response) => {
    setUserResponses(prev => ({
      ...prev,
      [scenarioId]: response
    }));
    
    // 保存到localStorage
    localStorage.setItem(`scenario_response_${scenarioId}`, response);
  };

  // 前往下一个场景
  const handleNextScenario = () => {
    if (currentScenarioIndex < scenarios.length - 1) {
      setCurrentScenarioIndex(prevIndex => prevIndex + 1);
      window.scrollTo(0, 0);
    } else {
      // 完成所有场景，保存所有回答并导航到结果页面
      saveFinalResults();
    }
  };

  // 返回上一个场景
  const handlePreviousScenario = () => {
    if (currentScenarioIndex > 0) {
      setCurrentScenarioIndex(prevIndex => prevIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  // 保存最终测试结果
  const saveFinalResults = async () => {
    setIsSubmitting(true);
    
    try {
      const testResults = {
        completedAt: new Date().toISOString(),
        scenarios: scenarios.map(scenario => ({
          id: scenario.id,
          title: scenario.title,
          response: userResponses[scenario.id] || ''
        }))
      };
      
      // 保存到localStorage以便向后兼容
      localStorage.setItem('customTestResult', JSON.stringify(testResults));
      
      // 如果用户已登录，保存到Supabase
      if (currentUser && currentUser.id) {
        await saveCustomTestResult(currentUser.id, testResults);
        console.log('自定义测试结果已保存到Supabase');
      }
      
      navigate('/analysis-report');
    } catch (error) {
      console.error('保存测试结果时出错:', error);
      // 即使有错误，仍然继续导航
      navigate('/analysis-report');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 加载中状态
  if (isLoading) {
    return (
      <div className="custom-test-container loading-container">
        <div className="loading-spinner"></div>
        <p>Preparing your personalized test...</p>
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="custom-test-container error-container">
        <div className="error-icon">⚠️</div>
        <h2>Error</h2>
        <p>{error}</p>
        <button 
          className="btn-primary" 
          onClick={() => navigate('/personal-questionnaire')}
        >
          Return to Questionnaire
        </button>
      </div>
    );
  }

  // 没有场景的状态
  if (scenarios.length === 0) {
    return (
      <div className="custom-test-container error-container">
        <div className="error-icon">⚠️</div>
        <h2>No Matching Scenarios</h2>
        <p>We couldn't find suitable scenarios for your profile. Please complete the questionnaire again or try later.</p>
        <button 
          className="btn-primary" 
          onClick={() => navigate('/personal-questionnaire')}
        >
          Retake Questionnaire
        </button>
      </div>
    );
  }

  // 渲染当前场景
  const currentScenario = scenarios[currentScenarioIndex];
  
  return (
    <div className="custom-test-container">
      <TestScenario
        scenario={currentScenario}
        onNext={handleNextScenario}
        onPrevious={handlePreviousScenario}
        onSaveResponse={handleSaveResponse}
        isFirst={currentScenarioIndex === 0}
        isLast={currentScenarioIndex === scenarios.length - 1}
        currentStep={currentScenarioIndex}
        totalSteps={scenarios.length}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}

export default CustomTest;



